import _ from 'lodash';
import moment from 'moment';

import referrals from '../../api/referrals';
import * as types from '../mutation-types';
import buildFilter from '../../utils/referralFilterBuilder';
import documents from '../../api/documents';
import activity from '../../api/activity';
import { referralTermsBuilder } from '@/utils/termsBuilder';

const state = {
  page: [],
  currentPage: 1,
  pageSize: 15,
  lastPage: 1,
  total: 0,
  orderBy: 'created_at',
  orderDirection: 'desc',
  loading: false,
  search: '',
  filters: {
    bool: {
      toBeCalled: false
    },
    noPhone: false,
    withPhone: false,
    noEmail: false,
    withEmail: false,
    closed: false,
    open: false,
    referred: false,
    selfReferred: false,
    hasPhone: false,
    eliteReferrer: false,
    registered: false,
    notRegistered: false,
    questionsComplete: false,
    questionsIncomplete: false,
    autoSendEnquiries: false,
    notAutoSendEnquiries: false,
    noEnquiries: false,
    owners: [],
    ownerNotAssigned: false,
    enquiryStatuses: [],
    referrerType: null,
    legalStatus: null,
    referrer: null,
    funded: null,
    funding: {
      minimum: 0,
      maximum: 50000000
    },
    dates: {
      from: '2016-05-01',
      to: moment().format('YYYY-MM-DD')
    }
  },
  referrals: [
    {
      id: null,
      company_name: '',
      referrer: {
        business_type: null
      },
      phone_calls: []
    }
  ],
  referrer_id: null,
  selected: {
    id: null,
    company_name: null,
    address: {
      line1: null,
      line2: null,
      line3: null,
      line4: null,
      line5: null,
      postcode: null
    },
    customer: {}
  },
  activity: [],
  documents: [],
  uploads: [],
  downloading: false,
  loadingReferral: true,
  scrollPosition: 0
};

const getters = {
  LoadingReferral: state => state.loadingReferral,
  ReferralActivity: state => state.activity,
  ReferralCurrentPage: state => state.currentPage,
  ReferralDocuments: state => state.documents,
  ReferralDownloadingState: state => state.downloading,
  ReferralLastPage: state => state.lastPage,
  ReferralLoadingState: state => state.loading,
  ReferralPage: state => state.page,
  ReferralPageFilters: state => state.filters,
  Referrals: state => state.referrals,
  ReferralSearch: state => state.search,
  ReferralTotal: state => state.total,
  ReferralUploads: state => state.uploads,
  SelectedReferral: state => state.selected,
  ReferralPageScrollPosition: state => state.scrollPosition
};

const actions = {
  async getReferralsPage({ commit, state }, append = false) {
    if (append) {
      commit(types.SET_REFERRAL_CURRENT_PAGE, state.currentPage + 1);
    } else {
      commit(types.SET_REFERRAL_CURRENT_PAGE, 1);
      commit(types.RESET_REFERRAL_TOTAL);
    }

    if (append && state.lastPage === 1) {
      return true;
    }

    const page = state.currentPage;
    const orderBy = [{ [state.orderBy]: state.orderDirection }];
    const filters = buildFilter(state.filters, state.search);
    const dates = state.filters.dates;
    const search = _.isNull(state.search) ? '*' : state.search;
    const fields = [];
    const terms = referralTermsBuilder(state.filters);

    const ranges = [
      {
        requested_amount: {
          gte: state.filters.funding.minimum || null,
          lte: state.filters.funding.maximum
        }
      }
    ];

    try {
      const response = await referrals.getPage(
        page,
        state.pageSize,
        orderBy,
        search,
        filters,
        terms,
        ranges,
        dates,
        fields
      );

      if (append) {
        commit(types.APPEND_REFERRAL_PAGE, response);
      } else {
        commit(types.SET_REFERRAL_PAGE, response);
      }
    } catch (error) {
      console.log(error);
      throw error;
    }

    return state.currentPage === state.lastPage;
  },

  getReferralActivity({ commit }, id) {
    return activity.getComments(id).then(response => {
      commit(types.SET_REFERRAL_ACTIVITY, response);
    });
  },

  downloadReferrals({ dispatch, state }) {
    return new Promise((resolve, reject) => {
      referrals
        .downloadReferrals(state.referrer_id)
        .then(response => {
          let blob = new Blob([response.data], { type: response.headers['content-type'] });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = 'referrals.csv';
          link.click();
          dispatch('setReferralDownloadingState', false);
          resolve();
        })
        .catch(error => reject(error));
    });
  },

  setReferralDownloadingState({ commit }, loadingState) {
    commit(types.SET_REFERRAL_DOWNLOADING_STATE, loadingState);
  },

  setReferralFilters({ commit, dispatch }, filters) {
    commit(types.SET_REFERRAL_FILTERS, filters);
    dispatch('getReferralsPage');
  },

  setReferralSearch({ commit }, search) {
    commit(types.SET_REFERRAL_SEARCH, search);
  },

  selectReferral({ commit, dispatch }, id) {
    const fields = ['*'];
    return referrals
      .get(id, fields)
      .then(apiResponse => {
        const referral = apiResponse._source;
        commit(types.SELECT_REFERRAL, referral);
        const subActions = [dispatch('getReferralActivity', id), dispatch('getReferralDocuments', id)];

        if (referral.customer.id) {
          subActions.push(dispatch('getReferralEnquiries', referral.customer.id));
        }

        return Promise.all(subActions);
      })
      .then(() => dispatch('stopLoadingReferral'))
      .catch(error => console.log(error));
  },

  clearSelectedReferral({ commit }) {
    commit(types.CLEAR_SELECTED_REFERRAL);
  },

  setReferrerId({ commit }, id) {
    commit(types.SET_REFERRER_ID, id);
  },

  updateReferral({ commit }, { id, updates }) {
    referrals
      .updateReferral(id, updates)
      .then(({ data }) => {
        commit(types.SET_UPDATE_REFERRAL, data);
      })
      .catch(error => console.log(error));
  },

  addReferral({ dispatch }, referral) {
    referrals
      .addReferral(referral)
      .then(() => {
        setTimeout(() => dispatch('getReferralsPage'), 3600);
      })
      .catch(error => console.log(error));
  },

  postReferralComment({ commit, rootState }, { comment, privacy }) {
    referrals.postReferralComment(rootState.referrals.selected.id, comment, privacy).then(({ data }) => {
      commit(types.POST_REFERRAL_COMMENT, data);
    });
  },

  uploadReferrals({ commit, dispatch }, { files, businessId }) {
    return referrals
      .uploadReferral(files, businessId)
      .then(response => {
        commit(types.SET_REFERRAL_UPLOADS, response);
        setTimeout(() => dispatch('getReferralsPage'), 1500);
      })
      .catch(error => console.log(error));
  },

  getReferralDocuments({ commit }, id) {
    const ids = {
      id: id
    };
    return documents.getDocuments(ids).then(documents => commit(types.SET_REFERRAL_DOCUMENTS, documents.data));
  },

  clearReferralUploads({ commit }) {
    commit(types.CLEAR_REFERRAL_UPLOADS);
  },

  setReferralCurrentPage({ commit }, page) {
    commit(types.SET_REFERRAL_CURRENT_PAGE, page);
  },

  setReferralPageFromDate({ commit }, from) {
    commit(types.SET_REFERRAL_PAGE_FROM_DATE, from);
  },

  setReferralPageToDate({ commit }, to) {
    commit(types.SET_REFERRAL_PAGE_TO_DATE, to);
  },

  setReferralPageFundingMinimum({ commit }, amount) {
    if (amount > state.filters.funding.maximum) {
      amount = state.filters.funding.maximum;
    }
    commit(types.SET_REFERRAL_PAGE_FUNDING_MINIMUM, amount);
  },

  setReferralPageFundingMaximum({ commit }, amount) {
    if (amount < state.filters.funding.minimum) {
      amount = state.filters.funding.minimum;
    }
    if (amount > 50000000) {
      amount = 50000000;
    }
    commit(types.SET_REFERRAL_PAGE_FUNDING_MAXIMUM, amount);
  },

  toggleReferralPageFilter({ commit }, filter) {
    return new Promise(resolve => {
      commit(types.TOGGLE_REFERRAL_PAGE_FILTER, filter);
      resolve();
    });
  },

  addReferralEnquiryStatusFilter({ commit }, status) {
    commit(types.ADD_REFERRAL_ENQUIRY_STATUS_FILTER, status);
  },

  removeReferralEnquiryStatusFilter({ commit }, status) {
    commit(types.REMOVE_REFERRAL_ENQUIRY_STATUS_FILTER, status);
  },

  addReferralOwnerFilter({ commit }, status) {
    commit(types.ADD_REFERRAL_OWNER_FILTER, status);
  },

  removeReferralOwnerFilter({ commit }, status) {
    commit(types.REMOVE_REFERRAL_OWNER_FILTER, status);
  },

  stopLoadingReferral({ commit }) {
    commit(types.STOP_LOADING_REFERRAL);
  },

  resetReferralFilters({ commit, dispatch }, refresh = true) {
    commit(types.RESET_REFERRAL_FILTERS);
    return refresh ? dispatch('getReferralsPage') : true;
  },

  setReferralScrollPosition({ commit }, position) {
    commit(types.SET_REFERRALS_SCROLL_POSITION, position);
  }
};

const mutations = {
  [types.SET_REFERRAL_CURRENT_PAGE](state, page) {
    state.currentPage = page;
  },
  [types.RESET_REFERRAL_TOTAL](state) {
    state.total = null;
  },
  [types.SET_REFERRAL_PAGE](state, response) {
    state.page = response.hits;
    state.total = response.total > 1000 ? 1000 : response.total;
    state.lastPage = Math.ceil(state.total / state.pageSize);
  },
  [types.APPEND_REFERRAL_PAGE](state, response) {
    state.page = [...state.page, ...response.hits];
  },
  [types.SET_REFERRAL_PAGE_FROM_DATE](state, from) {
    state.filters.dates.from = from;
  },
  [types.SET_REFERRAL_PAGE_TO_DATE](state, to) {
    state.filters.dates.to = to;
  },
  [types.SET_REFERRAL_PAGE_FUNDING_MINIMUM](state, amount) {
    state.filters.funding.minimum = amount;
  },
  [types.SET_REFERRAL_PAGE_FUNDING_MAXIMUM](state, amount) {
    state.filters.funding.maximum = amount;
  },
  [types.SET_REFERRALS](state, response) {
    state.referrals = response.data;
    state.currentPage = response.meta.current_page;
    state.lastPage = response.meta.last_page;
    state.total = response.meta.total;
  },
  [types.MAKE_REFERRAL](state, referral) {
    state.customer.push(referral.data);
  },
  [types.SET_REFERRAL](state, referral) {
    const record = _.find(state.customer, record => record.id === referral.data.id);
    _.merge(record, referral.data);
  },
  [types.SET_REFERRAL_PAGE_SIZE](state, pageSize) {
    state.pageSize = pageSize;
  },
  [types.SET_REFERRAL_ORDER_BY](state, orderBy) {
    state.orderBy = orderBy;
  },
  [types.SET_REFERRAL_SORT_DIRECTION](state, direction) {
    state.orderDirection = direction ? 'desc' : 'asc';
  },
  [types.SET_REFERRAL_FILTERS](state, filters) {
    _.merge(state.filters, filters);
  },
  [types.SET_REFERRAL_LOADING_STATE](state, loadingState) {
    state.loading = loadingState;
  },
  [types.SET_REFERRAL_SEARCH](state, search) {
    state.search = search;
  },
  [types.SELECT_REFERRAL](state, referral) {
    state.selected = referral;
  },
  [types.SET_REFERRAL_ACTIVITY](state, activity) {
    state.activity = _.map(activity.hits, '_source');
  },
  [types.SET_REFERRER_ID](state, id) {
    state.referrer_id = id;
  },
  [types.SET_UPDATE_REFERRAL](state, updated) {
    const referral = state.page.find(hit => hit._source.id === updated.id);

    if (referral) {
      _.merge(referral._source, updated);
    }

    if (state.selected.id === updated.id) {
      _.merge(state.selected, updated);
    }
  },
  [types.POST_REFERRAL_COMMENT](state, comment) {
    state.activity = [comment, ...state.activity];
  },
  [types.SET_REFERRAL_UPLOADS](state, response) {
    state.uploads = response.data;
  },
  [types.CLEAR_SELECTED_REFERRAL](state) {
    state.selected = {
      id: null,
      company_name: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        postcode: null
      },
      customer: {},
      email: null,
      first_name: null,
      last_name: null,
      phone: null,
      phone_calls: [],
      requested_amount: null,
      requested_on: null,
      required_by: null
    };
  },
  [types.CLEAR_REFERRAL_UPLOADS](state) {
    state.uploads = [];
  },
  [types.SET_REFERRAL_DOCUMENTS](state, documents) {
    state.documents = documents;
  },
  [types.SET_REFERRAL_DOWNLOADING_STATE](state, loadingState) {
    state.downloading = loadingState;
  },
  [types.TOGGLE_REFERRAL_PAGE_FILTER](state, filter) {
    if (_.startsWith(filter, 'bool')) {
      filter = _.split(filter, '.', 2)[1];
      state.filters['bool'][filter] = !state.filters['bool'][filter];
    } else {
      state.filters[filter] = !state.filters[filter];
    }
  },
  [types.ADD_REFERRAL_ENQUIRY_STATUS_FILTER](state, status) {
    state.filters.enquiryStatuses.push(status.id);
  },
  [types.REMOVE_REFERRAL_ENQUIRY_STATUS_FILTER](state, status) {
    state.filters.enquiryStatuses = _.filter(state.filters.enquiryStatuses, id => id !== status.id);
  },
  [types.ADD_REFERRAL_OWNER_FILTER](state, owner) {
    if (state.filters.owners.includes(null)) {
      state.filters.owners = [owner.id];
      state.filters.ownerNotAssigned = false;
    } else if (owner.id === null) {
      state.filters.owners = [null];
      state.filters.ownerNotAssigned = true;
    } else {
      state.filters.owners.push(owner.id);
      state.filters.ownerNotAssigned = false;
    }
  },
  [types.REMOVE_REFERRAL_OWNER_FILTER](state, owner) {
    if (state.filters.owners.includes(null)) {
      state.filters.ownerNotAssigned = false;
    }
    state.filters.owners = _.filter(state.filters.owners, id => id !== owner.id);
  },
  [types.START_LOADING_REFERRAL](state) {
    state.loadingReferral = true;
  },
  [types.STOP_LOADING_REFERRAL](state) {
    state.loadingReferral = false;
  },
  [types.RESET_REFERRAL_FILTERS](state) {
    state.filters = {
      bool: {
        toBeCalled: false
      },
      noPhone: false,
      noEmail: false,
      withEmail: false,
      closed: false,
      open: false,
      referred: false,
      selfReferred: false,
      hasPhone: false,
      eliteReferrer: false,
      registered: false,
      notRegistered: false,
      questionsComplete: false,
      questionsIncomplete: false,
      autoSendEnquiries: false,
      notAutoSendEnquiries: false,
      noEnquiries: false,
      owners: [],
      ownerNotAssigned: false,
      enquiryStatuses: [],
      referrerType: null,
      referrer: null,
      legalStatus: null,
      funded: null,
      funding: {
        minimum: 0,
        maximum: 50000000
      },
      dates: {
        from: '2016-05-01',
        to: moment().format('YYYY-MM-DD')
      }
    };
  },

  [types.SET_REFERRALS_SCROLL_POSITION](state, position) {
    state.scrollPosition = position;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
